import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { 
    Plus, 
    MapPin, 
    AlertTriangle, 
    Edit, 
    Trash2,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const LocationPicker = ({ position, setPosition }) => {
    useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        }
    });

    return position ? <Marker position={position} /> : null;
};

const IncidentsPage = () => {
    const { token, isChefDispositif, canManageIncidents } = useAuth();
    const { theme } = useTheme();
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingIncident, setEditingIncident] = useState(null);
    const [position, setPosition] = useState(null);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'autre',
        severity: 'modere',
        address: '',
        is_active: true
    });

    const headers = { Authorization: `Bearer ${token}` };

    const fetchIncidents = async () => {
        try {
            const response = await axios.get(`${API}/incidents`, { headers });
            setIncidents(response.data);
        } catch (error) {
            console.error('Failed to fetch incidents:', error);
            toast.error('Erreur lors du chargement des incidents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIncidents();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!position) {
            toast.error('Veuillez sélectionner une position sur la carte');
            return;
        }

        try {
            const data = {
                ...formData,
                latitude: position[0],
                longitude: position[1]
            };

            if (editingIncident) {
                await axios.put(`${API}/incidents/${editingIncident.id}`, data, { headers });
                toast.success('Incident mis à jour');
            } else {
                await axios.post(`${API}/incidents`, data, { headers });
                toast.success('Incident créé');
            }

            setDialogOpen(false);
            resetForm();
            fetchIncidents();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleEdit = (incident) => {
        setEditingIncident(incident);
        setFormData({
            title: incident.title,
            description: incident.description,
            type: incident.type,
            severity: incident.severity,
            address: incident.address,
            is_active: incident.is_active
        });
        setPosition([incident.latitude, incident.longitude]);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cet incident ?')) return;
        
        try {
            await axios.delete(`${API}/incidents/${id}`, { headers });
            toast.success('Incident supprime');
            fetchIncidents();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleToggleStatus = async (incident) => {
        const action = incident.is_active ? 'clore' : 'reactiver';
        if (!window.confirm(`Voulez-vous ${action} l'incident "${incident.title}" ?`)) return;
        try {
            const res = await axios.put(`${API}/incidents/${incident.id}/toggle-status`, {}, { headers });
            toast.success(res.data.message);
            fetchIncidents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            type: 'autre',
            severity: 'modere',
            address: '',
            is_active: true
        });
        setPosition(null);
        setEditingIncident(null);
    };

    const incidentTypes = [
        { value: 'incendie', label: 'Incendie' },
        { value: 'inondation', label: 'Inondation' },
        { value: 'accident', label: 'Accident' },
        { value: 'tempete', label: 'Tempête' },
        { value: 'autre', label: 'Autre' }
    ];

    const severityOptions = [
        { value: 'critique', label: 'Critique', color: 'text-red-500' },
        { value: 'modere', label: 'Modéré', color: 'text-amber-500' },
        { value: 'faible', label: 'Faible', color: 'text-green-500' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="incidents-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="page-title">Gestion des incidents</h1>
                    <p className="page-subtitle">Créer et gérer les incidents en cours</p>
                </div>

                {canManageIncidents && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button className="tactical-btn" data-testid="add-incident-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouvel incident
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl bg-zinc-900 border-zinc-800">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold text-white">
                                    {editingIncident ? 'Modifier l\'incident' : 'Nouvel incident'}
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Titre</Label>
                                        <Input
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="tactical-input"
                                            placeholder="Ex: Incendie rue du Commerce"
                                            required
                                            data-testid="incident-title-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label className="form-label">Type</Label>
                                        <Select 
                                            value={formData.type} 
                                            onValueChange={(value) => setFormData({ ...formData, type: value })}
                                        >
                                            <SelectTrigger className="tactical-input" data-testid="incident-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {incidentTypes.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="form-group">
                                        <Label className="form-label">Sévérité</Label>
                                        <Select 
                                            value={formData.severity} 
                                            onValueChange={(value) => setFormData({ ...formData, severity: value })}
                                        >
                                            <SelectTrigger className="tactical-input" data-testid="incident-severity-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {severityOptions.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value} className={opt.color}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="form-group">
                                        <Label className="form-label">Adresse</Label>
                                        <Input
                                            value={formData.address}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            className="tactical-input"
                                            placeholder="Adresse de l'incident"
                                            data-testid="incident-address-input"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Description</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="tactical-input min-h-[80px]"
                                        placeholder="Description détaillée de l'incident..."
                                        required
                                        data-testid="incident-description-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label className="form-label flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Position (cliquez sur la carte)
                                    </Label>
                                    <div className="h-[250px] rounded-lg overflow-hidden border border-zinc-700">
                                        <MapContainer
                                            center={position || [46.603354, 1.888334]}
                                            zoom={position ? 12 : 6}
                                            className="h-full w-full"
                                        >
                                            <TileLayer
                                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; OpenStreetMap'
                                            />
                                            <LocationPicker position={position} setPosition={setPosition} />
                                        </MapContainer>
                                    </div>
                                    {position && (
                                        <p className="text-xs text-zinc-500 mt-1 font-mono">
                                            Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}
                                        </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="rounded border-zinc-700"
                                        data-testid="incident-active-checkbox"
                                    />
                                    <Label htmlFor="is_active" className="text-sm text-zinc-400">
                                        Incident actif
                                    </Label>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button 
                                        type="button" 
                                        variant="ghost"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        data-testid="incident-cancel-btn"
                                    >
                                        Annuler
                                    </Button>
                                    <Button type="submit" className="tactical-btn" data-testid="incident-submit-btn">
                                        {editingIncident ? 'Mettre à jour' : 'Créer l\'incident'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Incidents Table */}
            <Card className="content-card">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">Titre</TableHead>
                                <TableHead className="text-zinc-400">Type</TableHead>
                                <TableHead className="text-zinc-400">Sévérité</TableHead>
                                <TableHead className="text-zinc-400">Adresse</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                <TableHead className="text-zinc-400">Date</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {incidents.map((incident) => (
                                <TableRow 
                                    key={incident.id} 
                                    className="border-zinc-800"
                                    data-testid={`incident-row-${incident.id}`}
                                >
                                    <TableCell className="font-medium text-white">
                                        {incident.title}
                                    </TableCell>
                                    <TableCell className="text-zinc-300">
                                        {incidentTypes.find(t => t.value === incident.type)?.label || incident.type}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`status-badge ${incident.severity}`}>
                                            {incident.severity}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-zinc-400 max-w-[200px] truncate">
                                        {incident.address || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {incident.is_active ? (
                                            <span className="status-badge success flex items-center gap-1 w-fit">
                                                <CheckCircle className="w-3 h-3" />
                                                Actif
                                            </span>
                                        ) : (
                                            <span className="status-badge critical flex items-center gap-1 w-fit">
                                                <XCircle className="w-3 h-3" />
                                                Clos
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-zinc-500 font-mono text-sm">
                                        {new Date(incident.created_at).toLocaleDateString('fr-FR')}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {isChefDispositif && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleToggleStatus(incident)}
                                                    className={incident.is_active ? 'text-amber-500 hover:bg-amber-500/10' : 'text-green-500 hover:bg-green-500/10'}
                                                    title={incident.is_active ? 'Clore' : 'Reactiver'}
                                                    data-testid={`toggle-incident-${incident.id}`}
                                                >
                                                    {incident.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                                </Button>
                                            )}
                                            {canManageIncidents && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleEdit(incident)}
                                                    className="tactical-btn-ghost"
                                                    data-testid={`edit-incident-${incident.id}`}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {isChefDispositif && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(incident.id)}
                                                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                                    data-testid={`delete-incident-${incident.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {incidents.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Aucun incident enregistré
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default IncidentsPage;
